import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from 'src/app/services/user.service';
import { Subscription } from 'rxjs';
import { InstitutionService } from 'src/app/services/institution.service';
import Institution from 'src/app/interfaces/Institution';
import { Location } from '@angular/common';
import { ToastService } from 'src/app/services/toast.service';
import { UploadService } from 'src/app/services/upload.service';
import { LoadingService } from 'src/app/services/loading.service';
import { PromptService } from 'src/app/services/prompt.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { UrlService } from 'src/app/services/url.service';

@Component({
  selector: 'app-edit-institution',
  templateUrl: './edit-institution.component.html',
  styleUrls: ['./edit-institution.component.scss'],
})
export class EditInstitutionComponent implements OnInit, OnDestroy {
  isNewInstitution = false;
  approveMembers = false;
  step = 0;
  subscriptions: Subscription = new Subscription();
  currentUser;
  institution: Institution = {
    expertise: [],
    institutionName: null,
    owner: null,
    ownerMeta: {
      name: null,
    },
    summary: null,
    timestamp: null,
    bannerImage: null,
    id: null,
    website: null,
    logo: null,
  };
  editorModules = {
    toolbar: [['link', 'bold', 'italic', 'underline', 'strike', 'blockquote', { list: 'ordered' }, { list: 'bullet' }]],
  };
  tags = {
    standard: [
      'technology',
      'environmental-impacts',
      'governance',
      'markets-finance',
      'funding',
      'cdr-community',
      'social-licensing',
    ],
    approaches: ['microalgae', 'blue-carbon', 'macroalgal','alkalinity', 'co2-removal', 'storage', 'other'],
  };
  institutionMembers = [];

  institutions$;
  institutions: Array<Institution>;
  filteredInstitutions: Array<any>;
  selectedInstitution: Institution = null;
  searchInput: string = null;

  errorMsg = '';

  bannerPreview;
  bannerAttachment;
  logoPreview;
  logoAttachment;

  constructor(
    private route: ActivatedRoute,
    private userService: UserService,
    private institutionService: InstitutionService,
    private location: Location,
    private router: Router,
    private renderer: Renderer2,
    private toastService: ToastService,
    private uploadService: UploadService,
    private loadingService: LoadingService,
    private promptService: PromptService,
    private notificationService: NotificationsService,
    private urlService: UrlService
  ) { }

  ngOnInit() {
    // See if new institution
    this.route.queryParams.subscribe((params) => {
      if (params['new']) {
        this.isNewInstitution = params['new'];
      } else {
        if (params['id']) {
          this.institutionService.getCurrentInstitution(params['id']).subscribe((i) => {
            this.institution = i[0];
            this.institutionService.getInstitutionMembers(this.institution.id).subscribe((m) => {
              this.institutionMembers = m;
            });
          });
        }
        if (params['approveMembers']) {
          this.approveMembers = true;
        }
      }
    });

    // Get user
    this.subscriptions.add(
      this.userService.user.subscribe((u) => {
        if (u && u.uid) {
          this.currentUser = u;
        }
      })
    );

    this.getInstitutions();
  }

  getInstitutions() {
    this.institutions$ = this.institutionService.getInstitutions().subscribe((p) => {
      this.institutions = p;
      this.filteredInstitutions = p;

      this.filteredInstitutions.forEach((p) => {
        this.institutionService.getInstitutionMembers(p.id).subscribe((m) => {
          p.members = m.length;
        });
      });
    });
  }

  filterInstitutionNames(input) {
    this.filteredInstitutions = this.institutions.filter((p) => {
      if (p.institutionName) {
        return p.institutionName.toLowerCase().indexOf(input.toLowerCase()) !== -1;
      }
    });
  }

  selectInstitution(p) {
    this.selectedInstitution = p;
    this.searchInput = p.institutionName;
    if (this.currentUser.institutions.indexOf(this.selectedInstitution.id) !== -1) {
      this.errorMsg =
        this.currentUser.institutionRoles[this.selectedInstitution.id].status === 'pending'
          ? "You've already sent a request to join this institution!"
          : "You're already a member of this institution!";
    }
  }
  clearSelectedInstitution() {
    this.selectedInstitution = null;
    this.searchInput = null;
    this.errorMsg = '';
    const institutionNameInput = this.renderer.selectRootElement('.institution-name-wrap input');
    setTimeout(() => institutionNameInput.focus(), 0);
  }
  nextStep() {
    // if institution exists, add institution to user
    if (this.selectedInstitution) {
      // if user is not already a member of this institution
      if (this.currentUser.institutions.indexOf(this.selectedInstitution.id) === -1) {
        this.loadingService.startLoading();
        this.currentUser.institutions.push(this.selectedInstitution.id);
        this.currentUser.institutionRoles[this.selectedInstitution.id] = {
          role: 'member',
          status: 'pending',
        };
        this.userService.saveUser(this.currentUser.uid, this.currentUser).then(() => {
          this.toastService.setToast(
            '<b>Success!</b> We’ve sent your request to join ' + this.selectedInstitution.institutionName + '.'
          );
          this.loadingService.stopLoading();
          // this.router.navigateByUrl(`/institutions/${this.selectedInstitution.id}`);
          this.router.navigate(['/institutions/' + this.selectedInstitution.id]);
        });
      }

      // if institution doesn't exist, create new institution
    } else {
      this.loadingService.startLoading();
      this.institutions$.unsubscribe();
      this.institutionService.createInstitution(this.institution).then(
        (id: string) => {
          this.institution.id = id;
          this.institution.institutionName = this.searchInput;
          this.institution.owner = this.currentUser.uid;
          this.institution.ownerMeta.name = this.currentUser.displayName;
          this.institution.timestamp = new Date();
          if (this.currentUser.expertise) {
            this.institution.expertise = [...this.currentUser.expertise];
          }
          this.currentUser.institutions.push(id);
          this.currentUser.institutionRoles[id] = {
            role: 'admin',
            status: 'active',
          };
          this.institutionMembers = [this.currentUser];
          this.loadingService.stopLoading();
          this.step++;
          // this.getInstitutions();
        },
        (e) => console.error(e)
      );
    }
  }

  onFileChanged(event, type) {
    if (event.target.files && event.target.files[0]) {
      var reader = new FileReader();
      var t = this;
      reader.onload = function (e) {
        t[type + 'Preview'] = reader.result;
      };

      reader.readAsDataURL(event.target.files[0]); // convert to base64 string

      this[type + 'Attachment'] = event.target.files[0];
    }
  }

  // toggleApproach(a) {
  //   if (this.institution.approaches.indexOf(a) === -1) {
  //     this.institution.approaches.push(a);
  //   } else {
  //     this.institution.approaches.splice(this.institution.approaches.indexOf(a), 1);
  //   }
  // }
  // toggleTag(t) {
  //   if (this.institution.tags.indexOf(t) === -1) {
  //     this.institution.tags.push(t);
  //   } else {
  //     this.institution.tags.splice(this.institution.tags.indexOf(t), 1);
  //   }
  // }

  removeUser(m) {
    this.promptService.setPrompt({
      hidden: false,
      heading: 'Are you sure you want to remove ' + m.firstName + '?',
      body: null,
      confirmText: "Yes, I'm sure",
      rejectText: 'Nevermind',
    });
    this.promptService.userResponse.subscribe((res) => {
      if (res === 'confirm') {
        this.loadingService.startLoading();
        let user = { ...m };
        delete user.institutionRoles[this.institution.id];
        user.institutions.splice(user.institutions.indexOf(this.institution.id));
        user.optionsToggled = false;
        this.userService.saveUser(user.uid, user).then(() => {
          this.institutionService.getInstitutionMembers(this.institution.id).subscribe((m) => {
            this.institutionMembers = m;
            let expertise = [];
            this.institutionMembers.forEach((member) => {
              if (member.expertise) {
                member.expertise.forEach((e) => {
                  if (expertise.indexOf(e) === -1) {
                    expertise.push(e);
                  }
                });
              }
            });
            this.institution.expertise = [...expertise];
            this.institutionService.updateInstitution(this.institution.id, this.institution);
          });
          this.loadingService.stopLoading();
          this.promptService.clearPrompt();
        });
      } else if (res === 'reject') {
        this.promptService.clearPrompt();
      }
    });
  }

  toggleAdmin(m) {
    this.loadingService.startLoading();
    let user = { ...m };
    user.institutionRoles[this.institution.id].role =
      user.institutionRoles[this.institution.id].role === 'admin' ? 'member' : 'admin';
    user.optionsToggled = false;
    this.userService.saveUser(m.uid, user).then(() => {
      m.institutionRoles[this.institution.id].role =
        user.institutionRoles[this.institution.id].role === 'admin' ? 'member' : 'admin';
      m.optionsToggled = !m.optionsToggled;
      this.loadingService.stopLoading();
    });
  }

  approveRequest(m) {
    this.loadingService.startLoading();
    let user = { ...m };
    user.institutionRoles[this.institution.id].status = 'active';
    user.optionsToggled = false;
    this.userService.saveUser(m.uid, user).then(() => {
      m.institutionRoles[this.institution.id].status = 'active';
      m.optionsToggled = !m.optionsToggled;

      if (m.expertise.length) {
        m.expertise.forEach((e) => {
          if (this.institution.expertise.indexOf(e) === -1) {
            this.institution.expertise.push(e);
          }
        });
      }
      this.institutionService.updateInstitution(this.institution.id, this.institution).then(() => {
        this.notificationService.sendNotifications({
          type: 'approveRequest',
          name: this.institution.institutionName,
          user: m.uid,
          url: `/m/edit-institution?id=${this.institution.id}&approveMembers=true`,
        });
        this.notificationService.saveNotification(
          m.uid,
          this.currentUser.uid,
          `approved your request to join <a href="/institutions/${this.institution.id}">their institution</a>`,
          this.currentUser.displayName,
          this.currentUser.profilePicture
        );
        this.loadingService.stopLoading();
      });
    });
  }

  removeLogo() {
    this.logoPreview = null;
    this.logoAttachment = null;
    this.institution.logo = null;
  }

  removeBanner() {
    this.bannerPreview = null;
    this.bannerAttachment = null;
    this.institution.bannerImage = null;
  }

  exitModal() {
    this.loadingService.startLoading();
    this.institution.summary = this.institution.summary ? this.urlService.processContent(this.institution.summary) : null;
    if (this.bannerAttachment) {
      this.uploadService.uploadFile(this.bannerAttachment).then((bannerUrl) => {
        this.institution.bannerImage = bannerUrl;

        if (this.logoAttachment) {
          this.uploadService.uploadFile(this.logoAttachment).then((logoUrl) => {
            this.institution.logo = logoUrl;
            this.saveInstitution();
          });
        } else {
          this.saveInstitution();
        }
      });
    } else {
      this.saveInstitution();
    }
  }
  saveInstitution() {
    this.institutionService.updateInstitution(this.institution.id, this.institution).then(
      (res) => {
        this.userService.saveUser(this.currentUser.uid, this.currentUser).then(() => {
          this.loadingService.stopLoading();
          if (this.isNewInstitution) {
            this.notificationService.sendNotifications({
              type: 'newInstitutions',
              author: this.institution.owner,
              title: this.institution.institutionName,
              url: `/institutions/${this.institution.id}`,
            });
          }
          // this.router.navigateByUrl(`/institutions/${this.institution.id}`);
          this.router.navigate([
            '/institutions/' + (this.selectedInstitution ? this.selectedInstitution.id : this.institution.id),
          ]);
        });
      },
      (e) => console.error(e)
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
